import React, { Component } from 'react'
import { object, string } from 'prop-types'
import { Redirect } from 'react-router-dom'
import classnames from 'classnames'
import { injectIntl } from 'react-intl'
import {
  requestAll,
  requestWidgets,
  requestFolders,
  requestArticlesWithAggregations,
  requestProjects,
  requestProducts,
  requestCachedFolders,
  requestMedias,
  requestProfessionals,
  requestArchiveArticles
} from '../utils/requests'
import {
  trackingPageViews,
  urlPathToObject,
  objectToUrlPath,
  isAbleToSearchWithoutText,
  getQueryFromUrl,
  getQueryStrings,
  appendComponent,
  dispatchSearchResultsLoadedEvent,
  executePianoExperience } from '../helpers/common'
import {
  RESULTS_DATA,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  NUMERAL,
  FOLDERS,
  TYPES_WITH_FILTER,
  ALL,
  IMAGES,
  PROJECTS,
  PRODUCTS,
  PROFESSIONALS,
  ARTICLE_ARCHIVE,
  ARTICLES
} from '../utils/constants'
import { GalleryContext } from '../providers/GalleryProvider'
import ResponsiveLayout from '../components/ui/responsive-layout/ResponsiveLayout'
import SearchBarWithState from '../components/SearchBarWithState'
import MetaDataComponent from '../components/meta-data/MetaData'
import { searchBarMessages as messages } from '../components/messages'
import SearchHeader from '../components/results/SearchHeader'
import { translateExpertiseFilters, orderingResults, translateTagFilters } from './resultHelpers'

const gridAbleAggregations = [ALL, PROJECTS, PRODUCTS, IMAGES]

const getFoldersPage = (page, search) => {
  let pageString = page && page > 1 ? `?page=${page}` : ''
  let currentPage = page
  const params = new URLSearchParams(search)
  const pageParam = Number(params.get('page'))
  if (pageParam && pageParam > page) {
    pageString = pageParam > 1 ? `?page=${pageParam}` : ''
    currentPage = pageParam
  }
  return { pageString, currentPage }
}

const withResults = (WrappedComponent, type) => {
  class WrapperComponent extends Component {
    static contextType = GalleryContext;

    constructor (props) {
      super(props)
      this.state = {
        aggregations: EMPTY_OBJECT,
        totalDocs: 0,
        query: '',
        lastQueryUsed: '',
        error: false,
        resultsAreLoading: false,
        results: EMPTY_ARRAY,
        resultsGrouped: EMPTY_OBJECT,
        widgets: EMPTY_OBJECT,
        widgetsAreLoading: false,
        hasMore: true,
        filters: EMPTY_OBJECT,
        gridView: gridAbleAggregations.includes(type),
        pageStart: 1,
        tags: EMPTY_ARRAY,
        offices: EMPTY_ARRAY,
        staticData: EMPTY_OBJECT,
        metadata: EMPTY_OBJECT,
        redirectToHome: false
      }
    }

    static propTypes = {
      site: string,
      history: object,
      location: object
    }

    static defaultProps = {
      site: '',
      history: EMPTY_OBJECT,
      location: EMPTY_OBJECT
    }

    componentDidMount () {
      executePianoExperience(`search-${type}`)

      const { location } = this.props
      const query = getQueryFromUrl(location)
      const onRequest = () => {
        if (TYPES_WITH_FILTER.includes(type)) {
          const filtersObject = urlPathToObject(location, type)
          const filters = objectToUrlPath(filtersObject, type)
          return this[RESULTS_DATA[type].request](filters, null, query)
        }
        return this[RESULTS_DATA[type].request]()
      }

      if (query !== '') {
        this.setState({ query, lastQueryUsed: query, resultsAreLoading: true }, onRequest)
      } else if (isAbleToSearchWithoutText(type)) {
        this.setState({ resultsAreLoading: true }, onRequest)
      } else {
        this.setState({ redirectToHome: true })
      }

      document.addEventListener('searchResultsLoaded', () => {
        /* eslint-disable */
        window.MyAD?.renderBookmarkLinks()
        window.notifications?.setupEntityHints()
        window.notifications?.setupButtons()
        /* eslint-enable */
      })
    }

    componentDidUpdate () {
      const { location } = this.props
      window.onpopstate = () => {
        const query = getQueryFromUrl(location)
        const newState = type === FOLDERS
          ? { results: EMPTY_ARRAY, resultsAreLoading: true, totalDocs: 0, query }
          : { query }

        this.setState({ ...newState }, () => {
          if (TYPES_WITH_FILTER.includes(type)) {
            // covering cases of landings that use filters
            const filtersObject = urlPathToObject(location, type)
            const filters = objectToUrlPath(filtersObject, type)
            this[RESULTS_DATA[type].request](filters)
          } else if (query) {
            // covering all the cases of landings when having an active query
            this[RESULTS_DATA[type].request](query)
          } else if (isAbleToSearchWithoutText(type)) {
            // covering the cases of landings that don't match the previous conditions and are enabled to do a search without text
            this[RESULTS_DATA[type].request]()
          } else {
            // covering the cases of landings that don't support a search without text and the query is empty
            this.setState({ redirectToHome: true })
          }
        })
      }
    }

    // Send metrics track events to GA and Insights
    sendTrackMetrics = (label, value) => {
      trackingPageViews()
      this.sendInsightsEvent(label, value)
    }

    sendInsightsEvent = (label, value) => {
      window.adInsights.e(`search-${type}`, 'search-unique', label, value, 1)
    }

    handleSearch = (query, typeOverride = null) => {
      const searchType = typeOverride || type
      const { lastQueryUsed, error } = this.state
      const { history, site } = this.props
      if (lastQueryUsed !== query) {
        const sitePath = site === 'us' ? '' : `/${site}`
        const queryString = query ? `?q=${query}` : ''
        const newPath = `${sitePath}/${RESULTS_DATA[searchType].searchType}${queryString}`
        if (error) {
          this.setState({ error: false })
        }
        if (TYPES_WITH_FILTER.includes(searchType)) {
          const filtersObject = urlPathToObject(location, searchType)
          const filters = objectToUrlPath(filtersObject, searchType)
          this.setState({ query }, () => {
            history.push(`${sitePath}/${searchType}${filters}${queryString}`)
            this[RESULTS_DATA[searchType].request](filters, null, query)
          })
        } else if (searchType === FOLDERS) {
          history.push(newPath)
          this.setState({ results: EMPTY_ARRAY, resultsAreLoading: true, totalDocs: 0, query }, () => {
            this[RESULTS_DATA[searchType].request](query, 1)
          })
        } else if (searchType === ARTICLES) {
          this.setState({ query }, () => {
            history.push(newPath)
            this[RESULTS_DATA[searchType].request](query)
          })
        } else {
          history.push(newPath)
          this[RESULTS_DATA[searchType].request](query)
        }
      }
    }

    // eslint-disable-next-line react/destructuring-assignment
    allRequestResults = (query = this.state.query) => {
      const { location: { search }, site } = this.props
      this.setState({ lastQueryUsed: query })
      if (query !== '') {
        this.setState({ query, resultsAreLoading: true }, () => {
          let page = ''
          const params = new URLSearchParams(search)
          const queryParam = params.get('q')

          if (queryParam === query) {
            page = Number(params.get('page'))
          }
          const urlParams = { site, query, oldState: this.state, page }
          requestAll(urlParams, newState => {
            this.sendTrackMetrics(query, newState.totalDocs)
            this.setState({ ...newState })
            dispatchSearchResultsLoadedEvent()
          })
          this.onRequestWidgets(page)
        })
      }
    }

    onRequestWidgets = (page = 1) => {
      const { site } = this.props
      const { query, widgets, gridView } = this.state
      const isSameQuery = widgets && widgets.for === query
      const shouldRequest = (site === 'us' && gridView) || !isSameQuery || Object.keys(widgets).length === 0

      if (shouldRequest) {
        requestWidgets({ site, query, page }, newState => {
          this.setState({ ...newState })
        })
      }
    }

    // eslint-disable-next-line react/destructuring-assignment
    articlesRequestResults = (query = this.state.query) => {
      const { site } = this.props
      this.setState({ lastQueryUsed: query })
      if (query !== '') {
        this.setState({ resultsAreLoading: true }, () => {
          const urlParams = { site, query, oldState: this.state }
          requestArticlesWithAggregations(urlParams, newState => {
            this.sendTrackMetrics(query, newState.totalDocs)
            this.setState({ ...newState })
            dispatchSearchResultsLoadedEvent()
          })
        })
      }
    }

    // eslint-disable-next-line react/destructuring-assignment
    articleArchiveRequestResults = (filters, initialPage = null, query = this.state.query, sort = null) => {
      query && (initialPage = 1)
      // eslint-disable-next-line react/prop-types
      const { site, location: { search }, intl } = this.props
      const params = new URLSearchParams(search)
      const page = initialPage || Number(params.get('page')) || ''
      this.setState({ lastQueryUsed: query, resultsAreLoading: true }, () => {
        const urlParams = { site, query, page, filters, sort }
        requestArchiveArticles(urlParams, newState => {
          this.setState({
            ...newState,
            filters: translateTagFilters(newState.filters, intl)
          })
          dispatchSearchResultsLoadedEvent()
        })
      })
    }

    // eslint-disable-next-line react/destructuring-assignment
    projectsRequestResults = (filters, initialPage = null, query = this.state.query) => {
      query && (initialPage = 1)
      const { site, location: { search } } = this.props
      const params = new URLSearchParams(search)
      const page = initialPage || Number(params.get('page')) || ''
      this.setState({ lastQueryUsed: query, resultsAreLoading: true }, () => {
        const urlParams = { site, query, page, filters }
        requestProjects(urlParams, newState => {
          this.sendTrackMetrics(query, newState.totalDocs)
          this.setState({ ...newState })
          dispatchSearchResultsLoadedEvent()
        })
      })
    }

    // eslint-disable-next-line react/destructuring-assignment
    productsRequestResults = (filters, initialPage = null, query = this.state.query) => {
      query && (initialPage = 1)
      const { site, location: { search } } = this.props
      const params = new URLSearchParams(search)
      const page = initialPage || Number(params.get('page')) || ''

      this.setState({ lastQueryUsed: query, resultsAreLoading: true }, () => {
        const urlParams = { site, query, page, filters }
        requestProducts(urlParams, newState => {
          this.sendTrackMetrics(query, newState.totalDocs)
          this.setState({ ...newState })
          dispatchSearchResultsLoadedEvent()
        })
      })
    }

    // eslint-disable-next-line react/destructuring-assignment
    mediasRequestResults = (filters, initialPage = null, query = this.state.query) => {
      query && (initialPage = 1)
      const { site, location: { search } } = this.props
      const params = new URLSearchParams(search)
      const page = initialPage || Number(params.get('page')) || ''
      const galleryContext = this.context

      this.setState({ lastQueryUsed: query, resultsAreLoading: true }, () => {
        const urlParams = { site, query, page, filters }
        requestMedias(urlParams, newState => {
          this.sendTrackMetrics(query, newState.totalDocs)
          // Update gallery images every time we get new results for same search
          galleryContext.addResults(newState.results, 1, true)
          this.setState({ ...newState })
          dispatchSearchResultsLoadedEvent()
        })
      })
    }

    // eslint-disable-next-line react/destructuring-assignment
    professionalsRequestResults = (filters, initialPage = null, query = this.state.query) => {
      query && (initialPage = 1)
      // eslint-disable-next-line react/prop-types
      const { site, location: { search }, intl } = this.props
      const params = new URLSearchParams(search)
      const page = initialPage || Number(params.get('page')) || ''

      this.setState({ lastQueryUsed: query, resultsAreLoading: true }, () => {
        const urlParams = { site, query, page, filters }
        requestProfessionals(urlParams, newState => {
          const formattedResults = {
            ...newState,
            results: orderingResults(newState.results),
            filters: translateExpertiseFilters(newState.filters, intl)
          }
          this.sendTrackMetrics(query, newState.totalDocs)
          this.setState({ ...formattedResults })
          dispatchSearchResultsLoadedEvent()
        })
      })
    }

    // eslint-disable-next-line react/destructuring-assignment
    foldersRequestResults = (query = this.state.query, page = 1, loadNextPage) => {
      const { results } = this.state
      const { site, history, location: { search } } = this.props
      const newPath = getQueryStrings(query, page)
      const { pageString, currentPage } = getFoldersPage(page, search)
      const isFirstPage = page === 1
      if (query !== '') {
        const urlParams = { page: currentPage, site, query }
        requestFolders(urlParams, newState => {
          const updatedResults = results.concat(newState.results)
          const newUpdatedState = {
            ...newState,
            results: updatedResults,
            lastQueryUsed: query,
            pageStart: currentPage,
            hasMore: !(updatedResults.length === newState.totalDocs)
          }
          this.sendTrackMetrics(query, newState.totalDocs)
          loadNextPage && history.push({ search: newPath })
          this.setState({ ...newUpdatedState })
          isFirstPage && dispatchSearchResultsLoadedEvent()
        })
      } else {
        const urlParams = { site, pageString, currentPage }
        requestCachedFolders(urlParams, newState => {
          this.sendTrackMetrics(query, newState.totalDocs)
          loadNextPage && history.push({ search: newPath })
          this.setState({
            ...newState,
            pageStart: currentPage,
            results: results.concat(newState.results)
          })
          isFirstPage && dispatchSearchResultsLoadedEvent()
        })
      }
    }

    handleFolderWidgetError = () => {
      this.setState(state => ({
        widgets: {
          ...state.widgets,
          folders: {
            results: EMPTY_ARRAY,
            view_more: NUMERAL
          }
        }
      }))
    }

    setView = isGrid => {
      const { history, location } = this.props
      const params = new URLSearchParams(location.search)
      params.delete('page')
      history.push({ search: params.toString() })
      this.setState({ gridView: isGrid })
    }

    onSuggestionSelected = (filter, query = '') => {
      const { history, site } = this.props
      const sitePath = site === 'us' ? '' : `/${site}`
      if (filter) {
        const slashCheckedFilters = filter.replace(' / ', ' slash ')
        history.push(`${sitePath}/${type}${slashCheckedFilters}`)
        this.mediasRequestResults(filter)
      } else {
        history.push(`${sitePath}/${ALL}?q=${query}`)
      }
    }

    clearFilters = () => {
      const { site, history, location } = this.props
      const filtersObject = urlPathToObject(location, type)
      if (Object.keys(filtersObject).length) {
        const { query } = this.state
        const queryString = query ? `?q=${query}` : ''
        const sitePath = site === 'us' ? '' : `/${site}`
        history.push(`${sitePath}/${type}${queryString}`)
        this[RESULTS_DATA[type].request]('')
      }
    }

    render () {
      const {
        query,
        widgetsAreLoading,
        widgets,
        results,
        resultsGrouped,
        recommendedResults,
        error,
        lastQueryUsed,
        resultsAreLoading,
        aggregations,
        totalDocs,
        hasMore,
        filters,
        gridView,
        pageStart,
        tags,
        offices,
        staticData,
        metadata,
        redirectToHome,
        requestKey
      } = this.state
      const { site } = this.props
      const searchBarComponent = (
        <SearchBarWithState
          onSearch={query => this.handleSearch(query, type === ARTICLE_ARCHIVE ? ALL : type)}
          query={type === ARTICLE_ARCHIVE ? undefined : query}
          placeholder={type === ARTICLE_ARCHIVE ? messages.default : messages[type]}
          aggregationActive={type}
          suggestions={type === IMAGES ? filters : null}
          onSuggestionSelected={this.onSuggestionSelected}
        />
      )
      if (redirectToHome) {
        const siteTo = site === 'us' ? '/' : '/' + site
        return <Redirect to={siteTo} />
      }
      const isGridAble = gridAbleAggregations.includes(type)
      const isNotIndexable = ((type === IMAGES || type === PROFESSIONALS) && !metadata.is_indexable)

      const mainDivClasses = classnames(
        'afd-container-main',
        'afd-container-main--margin-bottom',
        'nft-container-main-search',
        'clearfix',
        'afd-mobile-margin',
        'search-container',
        {
          'search-container--archive': type === ARTICLE_ARCHIVE
        }
      )

      return (
        <div className={mainDivClasses}>
          <MetaDataComponent
            metadata={metadata}
            shouldSetNoIndex={!!query || isNotIndexable}
            type={type} site={site}
          />
          <ResponsiveLayout
            renderLarge={() => appendComponent('jv-header__main-search-form', searchBarComponent)}
            renderMedium={() => appendComponent('afd-mobile-search-form__container', searchBarComponent)}
          />
          {!resultsAreLoading && totalDocs > 0 && (
            <SearchHeader
              staticData={staticData}
              aggregations={aggregations}
              aggregationActive={type}
              gridView={gridView}
              totalCount={totalDocs}
              lastQueryUsed={lastQueryUsed}
              filters={filters}
              requestResults={this[RESULTS_DATA[type].request]}
              setView={this.setView}
              isGridAble={isGridAble}
              tags={tags}
              metadata={metadata}
              handleSearch={this.handleSearch}
              {...this.props}
            />
          )}
          <WrappedComponent
            error={error}
            resultsAreLoading={resultsAreLoading}
            lastQueryUsed={lastQueryUsed}
            results={results}
            resultsGrouped={resultsGrouped}
            recommendedResults={recommendedResults}
            totalDocs={totalDocs}
            widgets={widgets}
            widgetsAreLoading={widgetsAreLoading}
            handleFolderWidgetError={this.handleFolderWidgetError}
            query={query}
            hasMore={hasMore}
            filters={filters}
            requestResults={this[RESULTS_DATA[type].request]}
            gridView={gridView}
            onRequestWidgets={this.onRequestWidgets}
            pageStart={pageStart}
            tags={tags}
            offices={offices}
            staticData={staticData}
            isGridAble={isGridAble}
            clearFilters={this.clearFilters}
            requestKey={requestKey}
            {...this.props}
          />
        </div>
      )
    }
  }

  return injectIntl(WrapperComponent)
}

export default withResults